import React, {useState} from "react";
import {Header, StyledCard, StyledForm, StyledTextField, Subtext,} from "./RequestPasswordReset.styled";
import {brand} from "../../../theme/brand";
import {AuthContainer} from "../Auth.styled";
import LogoWithLanguageSwitcher from '../../../components/common/LogoWithLanguageSwitcher';
import {useNavigate} from "react-router-dom";
import {useSnackbar} from "../../../contexts/SnackbarContext";
import {usePageTitle} from '../../../hooks/usePageTitle';
import {PageTitleKeys} from '../../../config/routeConstants';
import {Button, CircularProgress} from '@mui/material';
import {parseError} from "../../../errors/http.service";
import {useTranslation} from 'react-i18next';
import {getEmailValidationError} from "../../../utils/email-validation";
import {authService} from "../../../services/auth.service";

const RequestPasswordReset = () => {
    usePageTitle(PageTitleKeys.RequestPasswordReset);
    const {t} = useTranslation();
    const navigate = useNavigate();
    const { showSnackbar } = useSnackbar();

    const [email, setEmail] = useState("");
    const [emailTouched, setEmailTouched] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const emailValidationError = getEmailValidationError(email, true);
    const showEmailError = (emailTouched || submitted) && !!emailValidationError;

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        setSubmitted(true);
        if (emailValidationError) return;

        setIsLoading(true);
        try {
            await authService.requestPasswordReset(email);

            showSnackbar(t('requestPasswordReset.successMessage'), 'success');
            navigate("/check-mailbox");
        } catch (error) {
            console.error("Błąd resetowania hasła:", error);
            showSnackbar(parseError(error), "error");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <AuthContainer role="region" aria-labelledby="request-password-reset-title">
            <LogoWithLanguageSwitcher src={brand.logo} alt={t('auth.logo.alt')} clickable={true} />

            <StyledCard>
                <Header id="request-password-reset-title">
                    {t('requestPasswordReset.title')}
                </Header>
                <Subtext>
                    {t('requestPasswordReset.subtitle')}
                </Subtext>

                <StyledForm autoComplete="off" aria-live="polite" aria-atomic={true} aria-busy={isLoading} onSubmit={handleSubmit}>
                    <div>
                        <StyledTextField
                            type="text"
                            label={t('requestPasswordReset.email')}
                            value={email}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setEmail(e.target.value);
                            }}
                            onBlur={() => setEmailTouched(true)}
                            error={showEmailError}
                            helperText={showEmailError ? String(t(emailValidationError!.key)) : ""}
                            fullWidth
                            disabled={isLoading}
                            inputProps={{ 'aria-label': t('requestPasswordReset.email.ariaLabel') as string }}
                        />
                        <div style={{ height: '24px', marginTop: '4px' }}></div>
                    </div>

                    {isLoading && (
                        <div role="status" aria-live="polite" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(1px, 1px, 1px, 1px)' }}>
                            {t('requestPasswordReset.loading')}
                        </div>
                    )}

                    <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        disabled={isLoading || !email || showEmailError}
                        aria-label={t('requestPasswordReset.sendButton.ariaLabel') as string}
                    >
                        {isLoading ? <CircularProgress size={24} sx={{color: 'inherit'}}/> : t('requestPasswordReset.sendButton')}
                    </Button>
                </StyledForm>
            </StyledCard>
        </AuthContainer>
    );
};

export default RequestPasswordReset;
